import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { IngredientRecipeLite } from '../../../../../../../../shared/models/ingredient/ingredient-lite.model';
import { SearchService } from '../../../../../../../../services/search.service';
import { takeUntil } from 'rxjs/operators';
import { SearchPayload } from '../../../../../../../../shared/models/search.model';
import { Subject } from 'rxjs';
import { NotificationService } from '../../../../../../../../services/notification.service';

@Component({
  selector: 'app-base-preparation-block',
  templateUrl: './base-preparation-block.component.html',
  styleUrls: ['./base-preparation-block.component.scss'],
  standalone: false,
})
export class BasePreparationBlockComponent implements OnChanges, OnDestroy {
  @Input() formArray?: UntypedFormArray;
  @Input() domain = '';
  @Input() market = '';
  @Input() lang = '';

  formGroups?: UntypedFormGroup[];
  baseIngredients?: UntypedFormGroup[];
  ingredient?: UntypedFormGroup;
  recipesBase: IngredientRecipeLite[] = [];
  recipeTitles: string[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private searchService: SearchService,
    private readonly notificationService: NotificationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formArray && this.formArray) {
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
      this.baseIngredients = [];
      this.formGroups.forEach((formGroup) => {
        if (formGroup.value.ingredientRecipe) {
          this.baseIngredients?.push(formGroup);
        }
      });
    }
  }

  availableIngredients(): UntypedFormGroup[] {
    if (this.formGroups) {
      return this.formGroups.filter((ingredient) => !ingredient.value.ingredientRecipe && ingredient.value.applicationDescription);
    } else {
      return [];
    }
  }

  ingredientSelected(ingredient: UntypedFormGroup): void {
    ingredient.controls.ingredientRecipe.patchValue({} as IngredientRecipeLite);
  }

  delete(ingredient: UntypedFormGroup): void {
    ingredient.patchValue({ ingredientRecipe: undefined });
    ingredient.get('ingredientRecipe')?.markAsDirty();
    this.notificationService.sendWarning('RECIPES_EDIT.BASE.INFO_PREPARATION_DELETED');
  }

  search(searchInput: string): void {
    if (searchInput) {
      if (searchInput.length >= 3) {
        this.searchService
          .searchRecipe(this.buildSearchPayload(), {
            q: searchInput,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe((resultSearch) => {
            if (resultSearch.content) {
              this.recipesBase = [];
              this.recipeTitles = [];
              resultSearch.content.forEach((recipe) => {
                const recipeTitle = recipe.title + ' (' + recipe.yield.quantityDisplay + ' ' + recipe.yield.pluralName + ')';
                this.recipeTitles.push(recipeTitle);
                this.recipesBase.push({
                  fid: {
                    functionalId: recipe.identifier.functionalId,
                    sourceSystem: recipe.identifier.sourceSystem,
                    version: recipe.identifier.version,
                  },
                  status: recipe.status,
                  title: recipeTitle,
                } as IngredientRecipeLite);
              });
            }
          });
      } else {
        this.recipesBase = [];
        this.recipeTitles = [];
      }
    }
  }

  updateIngredientRecipe(ingredient: UntypedFormGroup): void {
    if (ingredient.value.ingredientRecipe.title !== null) {
      ingredient.patchValue({
        ingredientRecipe: this.recipesBase.find(
          (ingredientRecipeLite) => ingredientRecipeLite.title === ingredient.value.ingredientRecipe.title,
        ),
      });
      ingredient.get('ingredientRecipe')?.markAsDirty();
      this.notificationService.sendWarning('RECIPES_EDIT.BASE.INFO_PREPARATION_ADDED');
    }
  }

  private buildSearchPayload(): SearchPayload {
    return {
      fieldList: ['id', 'title', 'status', 'yieldDisplay', 'yield.unit'],
      facetList: [],
      fieldFilters: [
        { field: 'domain.key', values: [this.domain] },
        { field: 'market.key', values: [this.market] },
        { field: 'lang.key', values: [this.lang] },
        { field: 'classifications.type.key', values: ['BASIC_RECIPE'] },
        { field: 'status.key', values: ['DELETED'], type: 'exclusion' },
      ],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
